import { observer } from 'owa-mobx-react';
import { useComputedValue } from 'owa-react-hooks/lib/useComputed';
import { selectFolder, lazyToggleFavoriteFolder } from 'owa-mail-folder-forest-actions';
import { getSelectedNode } from 'owa-mail-folder-forest-store';
import type KeyboardCharCodes from 'owa-hotkeys/lib/utils/keyboardCharCodes';
import type { MailFolder, FolderForestTreeType } from 'owa-graph-schema';
import { isFolderInFavorites } from 'owa-favorites';
import { getMailboxInfoFromFolderId } from 'owa-mail-mailboxinfo';
import type { ChevronProps } from 'owa-tree-node/lib/components/TreeNode';
import TreeNode from 'owa-tree-node/lib/components/TreeNode';
import React, { useCallback, useRef } from 'react';
import { getStore } from 'owa-mail-folder-store/lib/store/store';
import { type FolderForestNodeType } from 'owa-favorites-types';
import type { ITouchHandlerParams } from 'owa-touch-handler';
import { touchHandler } from 'owa-touch-handler';
import { BulkActionSpinner, isBulkActionRunning } from 'owa-bulk-action-store';
import { UnreadReadCountBadge } from 'owa-unreadread-count-badge';
import {
    buildUnreadTotalCountTooltip,
    getDefaultFolderCountDisplayMode,
    isSearchFolderExpired,
} from 'owa-folders';
import { getFolderCountDisplayMode } from 'owa-mail-folder-store/lib/selectors/getFolderCountDisplayMode';
import { isGlobalFeatureEnabled } from 'owa-feature-flags';
import {
    PRIMARY_DELETED_ITEMS_DISTINGUISHED_ID,
    SEARCH_FOLDER_ROOT_DISTINGUISHED_ID,
    SourceMap,
} from 'owa-folders-constants';
import shouldShowAttachedInboxRulesDialog from '../helpers/shouldShowAttachedInboxRulesDialog';
import toggleFolderNodeExpansion from 'owa-mail-folder-store/lib/actions/toggleFolderNodeExpansion';
import { Spinner } from '@fluentui/react-components';
import type { FluentIcon } from '@fluentui/react-icons';
import { confirm, DialogResponse } from 'owa-confirm-dialog';
import loc from 'owa-localize';
import {
    expiredSearchFolder_tooltip,
    expiredSearchFolder_dialogTitle,
    expiredSearchFolder_dialogBody,
    expiredSearchFolder_reactivateButton,
    expiredSearchFolder_deleteButton,
} from './FolderNode.locstring.json';
import { lazyReactivateExpiredSearchFolder } from 'owa-mail-search-folder-view';

export interface FolderNodeProps extends React.HTMLProps<HTMLDivElement> {
    displayName: string;
    folderId: string;
    treeType: FolderForestTreeType;
    distinguishedFolderId: string;
    totalCount: number;
    unreadCount: number;

    // Optional parameters
    chevronProps?: ChevronProps;
    customIconComponent?: FluentIcon;
    depth?: number; // root is 0, every sub node increase this number by 1.
    isBeingDragged?: boolean;
    isDroppedOver?: boolean;
    onContextMenu?: (
        evt: React.MouseEvent<any> | React.TouchEvent<any>,
        folder?: MailFolder
    ) => void;
    showHoverStateOnDroppedOver?: boolean;
    isPublicFolder?: boolean;
    isLoading?: boolean;
    deleteFolder?: (resolveConfirmImmediately: boolean) => void;
    ellipsesOnHover?: boolean;
    customIconColor?: string;
    tooltipNameContent?: string;
    parentRef?: React.RefObject<HTMLDivElement>;
    nodeRef?: React.RefObject<HTMLDivElement>;
    isVerticalNode?: boolean;
    hideIcon?: boolean;
    customTreeNodeTooltip?: string;
}

const FolderNode: React.FC<FolderNodeProps> = ({
    displayName,
    folderId,
    treeType,
    distinguishedFolderId,
    totalCount,
    unreadCount,
    chevronProps,
    customIconComponent,
    depth,
    isBeingDragged,
    isDroppedOver,
    onContextMenu,
    showHoverStateOnDroppedOver,
    isPublicFolder,
    isLoading,
    deleteFolder,
    ellipsesOnHover,
    customIconColor,
    tooltipNameContent,
    parentRef,
    nodeRef,
    isVerticalNode,
    hideIcon,
    customTreeNodeTooltip,
}) => {
    const isSelected = useComputedValue((): boolean => {
        const selectedNode = getSelectedNode();
        return treeType == selectedNode?.treeType && folderId == selectedNode?.id;
    }, [treeType, folderId]);

    const isWithContextMenuOpen = useComputedValue((): boolean => {
        const store = getStore();
        return (
            folderId == store.withContextMenuFolderId &&
            treeType == store.contextMenuState?.treeType
        );
    }, [folderId, treeType]);

    const onTouchHandler = useComputedValue(() => {
        const touchHandlerParams: ITouchHandlerParams = {
            onLongPress: onContextMenu,
        };
        return touchHandler(touchHandlerParams);
    }, [onContextMenu]);

    const isSearchFolderRoot = useComputedValue((): boolean => {
        return distinguishedFolderId == SEARCH_FOLDER_ROOT_DISTINGUISHED_ID;
    }, [distinguishedFolderId]);

    const renderUnreadOrTotalCount = useCallback((): JSX.Element | null => {
        const effectiveMode =
            getFolderCountDisplayMode(folderId) ??
            getDefaultFolderCountDisplayMode(distinguishedFolderId);
        const shouldDisplayTotalCount = effectiveMode === 'total' && totalCount > 0;
        const count = shouldDisplayTotalCount ? totalCount : unreadCount;

        return count ? (
            <UnreadReadCountBadge count={count} shouldDisplayTotalCount={shouldDisplayTotalCount} />
        ) : null;
    }, [distinguishedFolderId, totalCount, unreadCount, folderId]);

    const renderRightCharm = useCallback((): JSX.Element | null => {
        if (isLoading) {
            return <Spinner size="extra-tiny" />;
        }

        if (isBulkActionRunning(folderId)) {
            return <BulkActionSpinner folderId={folderId} folderName={displayName} />;
        }

        return renderUnreadOrTotalCount();
    }, [folderId, isLoading, displayName, renderUnreadOrTotalCount]);

    const isExpiredFolder = useComputedValue((): boolean => {
        return isGlobalFeatureEnabled('fp-searchfolder-expiry') && isSearchFolderExpired(folderId);
    }, [folderId]);

    const isReactivatingRef = useRef(false);

    const onClick = useCallback(
        async (evt: React.MouseEvent<unknown>) => {
            evt.stopPropagation();
            // The Search Folders node toggles its subtree expansion state instead of selecting the folder
            if (isSearchFolderRoot) {
                // If the user has no search folders, pull up the create search folder dialog
                if (!chevronProps) {
                    onContextMenu?.(evt);
                }
                toggleFolderNodeExpansion(folderId);
            } else if (isExpiredFolder && !isReactivatingRef.current) {
                isReactivatingRef.current = true;
                const response = await confirm(
                    loc(expiredSearchFolder_dialogTitle),
                    loc(expiredSearchFolder_dialogBody),
                    false,
                    {
                        okText: loc(expiredSearchFolder_reactivateButton),
                        cancelText: loc(expiredSearchFolder_deleteButton),
                        useV9Dialog: true,
                    }
                );
                if (response === DialogResponse.ok) {
                    const mailboxInfo = getMailboxInfoFromFolderId(folderId);
                    if (mailboxInfo) {
                        lazyReactivateExpiredSearchFolder
                            .importAndExecute(folderId, mailboxInfo)
                            .catch(() => {})
                            .finally(() => {
                                isReactivatingRef.current = false;
                            });
                    } else {
                        isReactivatingRef.current = false;
                    }
                } else if (response === DialogResponse.cancel) {
                    deleteFolder?.(false /* resolveConfirmImmediately */);
                    isReactivatingRef.current = false;
                } else {
                    isReactivatingRef.current = false;
                }
            } else {
                /**
                 * selectFolder, even if it's the same folder users are already selected on
                 * the action has several side effects that users are counting on, including that
                 * if users are in immersive reader view, the reading pane is closed
                 */
                selectFolder(
                    folderId,
                    treeType,
                    'FolderNodeClick' /* actionSource */,
                    isPublicFolder ? 5 : 0,
                    undefined,
                    evt.nativeEvent.timeStamp
                );
            }
        },
        [
            isSearchFolderRoot,
            isExpiredFolder,
            chevronProps,
            onContextMenu,
            folderId,
            treeType,
            isPublicFolder,
        ]
    );

    const onKeyDown = useCallback(
        async (evt: React.KeyboardEvent<unknown>) => {
            switch (evt.keyCode) {
                case 13:
                case 32:
                    evt.stopPropagation();

                    // explicity cancelling event so click won't fire after this in case when folder pane is opened as overlay.
                    // only stopPropagation is not enough
                    evt.preventDefault();
                    selectFolder(
                        folderId,
                        treeType,
                        'Keyboard' /* actionSource */,
                        undefined,
                        undefined,
                        evt.nativeEvent.timeStamp
                    );
                    break;
                case 46:
                    if (
                        await shouldShowAttachedInboxRulesDialog(
                            () => deleteFolder?.(true /* resolveConfirmImmediately */),
                            PRIMARY_DELETED_ITEMS_DISTINGUISHED_ID,
                            folderId,
                            'Keyboard' /* actionSource */
                        )
                    ) {
                        break;
                    }
                    deleteFolder?.(false /* resolveConfirmImmediately */);
                    break;
            }
        },
        [folderId, treeType, deleteFolder]
    );

    const toggleFavorite = useCallback(() => {
        const isFavorite = isFolderInFavorites(folderId);
        lazyToggleFavoriteFolder.importAndExecute(folderId, isFavorite, 'ContextFolder');
    }, [folderId]);

    const computedMailboxInfo = useComputedValue(
        () => getMailboxInfoFromFolderId(folderId),
        [folderId]
    );
    const mailboxInfo = isGlobalFeatureEnabled('fwk-stabilize-folder-maillist')
        ? computedMailboxInfo
        : getMailboxInfoFromFolderId(folderId);

    return (
        <TreeNode
            {...onTouchHandler}
            chevronProps={chevronProps}
            customIconComponent={customIconComponent}
            depth={depth}
            displayName={displayName}
            isBeingDragged={isBeingDragged}
            isDroppedOver={isDroppedOver}
            isRootNode={false}
            isSelected={isSelected}
            isWithContextMenuOpen={isWithContextMenuOpen}
            key={folderId}
            onKeyDown={onKeyDown}
            onClick={onClick}
            onContextMenu={onContextMenu}
            renderRightCharm={renderRightCharm}
            showAsHoverOnDroppedOver={showHoverStateOnDroppedOver}
            isFavorited={isFolderInFavorites(folderId)}
            toggleFavorite={toggleFavorite}
            isinSharedFolderTree={treeType == 'sharedFolderTree'}
            isDisabled={isLoading}
            customTreeNodeTooltip={
                customTreeNodeTooltip ??
                (isSearchFolderRoot
                    ? undefined
                    : isExpiredFolder
                    ? loc(expiredSearchFolder_tooltip)
                    : buildUnreadTotalCountTooltip(
                          tooltipNameContent ?? displayName,
                          totalCount,
                          unreadCount
                      ))
            }
            ellipsesOnHover={ellipsesOnHover}
            isVerticalNode={isVerticalNode}
            hideIcon={hideIcon}
            source={SourceMap.subfolder}
            shouldShowFocusBorder={true}
            customIconColor={customIconColor}
            mailboxInfo={mailboxInfo}
            parentRef={parentRef}
            elementRef={nodeRef}
            useNewSelectionUI={isGlobalFeatureEnabled('fp-newfoldersselection-ui')}
        />
    );
};

const defaultExport: React.FunctionComponent<FolderNodeProps> = observer(FolderNode, 'FolderNode');
export default defaultExport;
